<div class="websites-page account-settings-page">
  @if (isInitialLoading) {
    <section class="page-card card">
      <div class="card-body py-5 text-center">
        <div class="spinner-border text-primary" role="status"></div>
        <p class="mt-3 mb-0">Loading websites...</p>
      </div>
    </section>
  } @else if (hasInitialLoadError) {
    <app-api-load-error
      title="Could not load websites"
      message="The websites inventory and page data could not be loaded right now."
      retryLabel="Retry"
      (retry)="loadWebsites()"
    ></app-api-load-error>
  } @else {
    <div class="d-grid gap-4">
      @if (pageMessage) {
        <div class="alert alert-info mb-0 websites-page__page-alert" role="status" aria-live="polite">
          <span>{{ pageMessage }}</span>
          <button
            type="button"
            class="websites-page__alert-close"
            aria-label="Dismiss message"
            title="Dismiss message"
            (click)="clearPageMessage()"
          >
            x
          </button>
        </div>
      }

      @if (websites.length === 0) {
        <section class="page-card card">
          <div class="card-body d-grid gap-3">
            <div>
              <h3 class="section-title mb-1 websites-page__title-line">
                <span>Websites</span>
                <app-info-tooltip
                  compact
                  placement="right"
                  title="Websites workspace"
                  text="Create the first website here. Once one exists, the workspace splits into an inventory card on the left and the active website workspace on the right."
                ></app-info-tooltip>
              </h3>
            </div>

            <div class="d-flex flex-wrap justify-content-between align-items-center gap-2">
              <span class="badge text-bg-secondary">{{ websiteCountLabel }}</span>
              <button type="button" class="btn btn-primary" (click)="openCreateWebsiteModal()">New website</button>
            </div>
          </div>
        </section>
      } @else {
        <div class="websites-page__workspace-shell">
          <section class="page-card card websites-page__inventory-card">
            <div class="card-body websites-page__inventory-body">
              <div class="websites-page__sidebar-header">
                <div>
                  <h4 class="section-title mb-1 websites-page__title-line">
                    <span>Website inventory</span>
                    <app-info-tooltip
                      compact
                      placement="right"
                      text="Each website in this list opens the full workspace on the right. Keep website names clear so the inventory stays easy to scan later."
                    ></app-info-tooltip>
                  </h4>
                </div>

                <div class="d-flex flex-wrap align-items-center gap-2">
                  <span class="badge text-bg-secondary">{{ websiteCountLabel }}</span>
                  <button type="button" class="btn btn-primary" (click)="openCreateWebsiteModal()">New website</button>
                </div>
              </div>

              <div class="websites-page__inventory-scroll">
                <div class="websites-page__website-list" role="listbox" aria-label="Website inventory">
                  @for (website of websites; track trackWebsite($index, website)) {
                    <button
                      type="button"
                      class="websites-page__website-item"
                      [class.is-active]="website.publicId === selectedWebsitePublicId"
                      [attr.aria-selected]="website.publicId === selectedWebsitePublicId"
                      (click)="selectWebsite(website.publicId)"
                    >
                      <div class="d-flex justify-content-between align-items-start gap-3">
                        <div>
                          <h5 class="websites-page__website-title mb-1">{{ website.name }}</h5>
                          <p class="websites-page__website-slug mb-0">/{{ website.slug }}</p>
                        </div>
                        <span class="badge" [class]="statusBadgeClass(website.status)">{{ website.status }}</span>
                      </div>

                      <div class="websites-page__website-meta">
                        <span>{{ website.pageCount }} pages</span>
                        <span>{{ website.subdomain || 'No subdomain' }}</span>
                      </div>
                    </button>
                  }
                </div>
              </div>
            </div>
          </section>

          <section class="page-card card websites-page__panel-card">
            <div class="card-body websites-page__panel-body">
              @if (selectedWebsite) {
                <div class="websites-page__panel-scroll">
                  <div class="websites-page__panel-header">
                    <div class="websites-page__panel-heading">
                      <div class="d-flex flex-wrap align-items-center gap-2 mb-2">
                        <h3 class="section-title mb-0 websites-page__title-line">
                          <span>{{ selectedWebsite.name }}</span>
                          <app-info-tooltip
                            compact
                            placement="right"
                            [text]="(selectedWebsite.description ? selectedWebsite.description + ' ' : '') + 'Use the workspace tabs below to manage pages, saved versions, deployments, and data.'"
                          ></app-info-tooltip>
                        </h3>
                        <span class="badge" [class]="statusBadgeClass(selectedWebsite.status)">{{ selectedWebsite.status }}</span>
                      </div>
                    </div>

                    <div class="d-flex flex-wrap gap-2">
                      <button type="button" class="btn btn-outline-primary" (click)="openWebsitePreview(selectedWebsite)">Preview website</button>
                      <button type="button" class="btn btn-outline-success" (click)="publishWebsite(selectedWebsite)" [disabled]="isPublishingWebsite">
                        {{ isPublishingWebsite ? 'Publishing...' : 'Publish website' }}
                      </button>
                      @if (selectedWebsite.subdomain) {
                        <button type="button" class="btn btn-outline-success" (click)="openLiveWebsite(selectedWebsite)">Open live site</button>
                      }
                      <button type="button" class="btn btn-outline-secondary" (click)="openWebsiteTranslations(selectedWebsite)">Translations</button>
                      <button type="button" class="btn btn-outline-secondary" (click)="openEditWebsiteModal(selectedWebsite)">Edit website</button>
                      <button type="button" class="btn btn-outline-danger" (click)="deleteWebsite(selectedWebsite)">Delete website</button>
                      <button type="button" class="btn btn-primary" (click)="openCreatePageModal()">New page</button>
                    </div>
                  </div>

                <div class="websites-page__details-grid">
                  <div class="websites-page__detail-tile">
                    <span class="websites-page__detail-label">Slug</span>
                    <strong>/{{ selectedWebsite.slug }}</strong>
                  </div>
                  <div class="websites-page__detail-tile">
                    <span class="websites-page__detail-label">Subdomain</span>
                    <strong>{{ selectedWebsite.subdomain || 'Not assigned' }}</strong>
                  </div>
                  <div class="websites-page__detail-tile">
                    <span class="websites-page__detail-label">Custom domain</span>
                    <strong>{{ selectedWebsite.customDomain || 'Not connected' }}</strong>
                  </div>
                  <div class="websites-page__detail-tile">
                    <span class="websites-page__detail-label">Pages</span>
                    <strong>{{ selectedWebsite.pageCount }}</strong>
                  </div>
                </div>

                <div class="websites-page__workspace-tabs">
                    <div class="websites-page__tabs" role="tablist" aria-label="Website workspace tabs">
                      @for (tab of workspaceTabs; track trackWorkspaceTab($index, tab)) {
                        <div class="websites-page__tab-header" [class.is-active]="activeWorkspaceTab === tab.id">
                          <button
                            type="button"
                            class="websites-page__tab"
                            [attr.aria-selected]="activeWorkspaceTab === tab.id"
                            [attr.tabindex]="activeWorkspaceTab === tab.id ? 0 : -1"
                            [id]="'website-workspace-tab-' + tab.id"
                            [attr.aria-controls]="'website-workspace-panel-' + tab.id"
                            role="tab"
                            (click)="setActiveWorkspaceTab(tab.id)"
                            (keydown)="handleWorkspaceTabKeydown($event, tab.id)"
                          >
                            {{ tab.label }}
                          </button>
                          <app-info-tooltip
                            class="websites-page__tab-help"
                            compact
                            placement="bottom"
                            [openDelay]="200"
                            [label]="tab.label + ' help'"
                            [text]="tab.helpText"
                          ></app-info-tooltip>
                        </div>
                      }
                    </div>

                  <div
                    class="websites-page__tab-panel"
                    [id]="'website-workspace-panel-' + activeWorkspaceTab"
                    [attr.aria-labelledby]="'website-workspace-tab-' + activeWorkspaceTab"
                    role="tabpanel"
                    tabindex="0"
                  >
                  @switch (activeWorkspaceTab) {
                    @case ('pages') {
                      <section class="websites-page__surface">
                        @if (isPagesLoading) {
                          <div class="py-4 text-center">
                            <div class="spinner-border text-primary" role="status"></div>
                            <p class="mt-3 mb-0">Loading pages...</p>
                          </div>
                        } @else if (pagesLoadError) {
                          <app-api-load-error
                            title="Could not load pages"
                            [message]="pagesLoadError"
                            retryLabel="Retry"
                            (retry)="loadWebsites()"
                          ></app-api-load-error>
                        } @else if (pages.length === 0) {
                          <div class="empty-state">This website has no pages yet. Add one to create its first editable draft.</div>
                        } @else {
                          <div class="table-responsive">
                            <table class="table align-middle mb-0 websites-page__pages-table">
                              <thead>
                                <tr>
                                  <th>Page</th>
                                  <th>Path</th>
                                  <th>Draft</th>
                                  <th>Published</th>
                                  <th>Updated</th>
                                  <th class="text-end">Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                @for (page of pages; track trackPage($index, page)) {
                                  <tr [class.table-active]="page.publicId === selectedPagePublicId" (click)="selectPage(page.publicId)">
                                    <td>
                                      <div class="fw-semibold d-flex flex-wrap align-items-center gap-2">
                                        <span>{{ page.name }}</span>
                                        @if (page.isHome) {
                                          <span class="badge text-bg-primary-subtle">Home</span>
                                        }
                                      </div>
                                    </td>
                                    <td>
                                      @if (isEditingPagePath(page)) {
                                        <div class="websites-page__path-editor" (click)="$event.stopPropagation()">
                                          <div class="websites-page__path-inline">
                                            <input
                                              class="form-control websites-page__path-input"
                                              type="text"
                                              [(ngModel)]="editingPagePath"
                                              [ngModelOptions]="{ standalone: true }"
                                              placeholder="/about"
                                              autocapitalize="off"
                                              spellcheck="false"
                                              (keydown.enter)="savePagePath(page); $event.preventDefault()"
                                              (keydown.escape)="cancelPagePathEdit(); $event.preventDefault()"
                                            />
                                            <button
                                              type="button"
                                              class="websites-page__icon-action is-success"
                                              [disabled]="isSavingPagePath"
                                              aria-label="Save page path"
                                              title="Save page path"
                                              (click)="savePagePath(page)"
                                            >
                                              <i class="fa-solid fa-check" aria-hidden="true"></i>
                                            </button>
                                            <button
                                              type="button"
                                              class="websites-page__icon-action"
                                              [disabled]="isSavingPagePath"
                                              aria-label="Cancel page path edit"
                                              title="Cancel"
                                              (click)="cancelPagePathEdit()"
                                            >
                                              <i class="fa-solid fa-xmark" aria-hidden="true"></i>
                                            </button>
                                          </div>
                                          @if (editingPagePathError) {
                                            <div class="websites-page__path-error">{{ editingPagePathError }}</div>
                                          }
                                        </div>
                                      } @else {
                                        <div class="websites-page__path-inline">
                                          <div class="websites-page__path-value">
                                            <span>{{ page.path }}</span>
                                            @if (page.isHome) {
                                              <span class="badge text-bg-primary-subtle">Root path</span>
                                            }
                                          </div>
                                          @if (!page.isHome) {
                                            <button
                                              type="button"
                                              class="websites-page__icon-action"
                                              aria-label="Edit page path"
                                              title="Edit page path"
                                              (click)="startPagePathEdit(page)"
                                            >
                                              <i class="fa-solid fa-pen" aria-hidden="true"></i>
                                            </button>
                                          }
                                        </div>
                                      }
                                    </td>
                                    <td>v{{ page.currentDraftVersion || 0 }}</td>
                                    <td>
                                      @if (page.currentPublishedVersion) {
                                        <span>v{{ page.currentPublishedVersion }}</span>
                                      } @else {
                                        <span class="text-body-secondary">Not published</span>
                                      }
                                    </td>
                                    <td>{{ page.updatedAt | date: 'mediumDate' }}</td>
                                    <td class="text-end">
                                      <div class="websites-page__row-actions" (click)="$event.stopPropagation()">
                                        <button type="button" class="btn btn-sm btn-outline-secondary" (click)="openPageBuilder(page)">Open builder</button>
                                        <button type="button" class="btn btn-sm btn-outline-danger" (click)="deletePage(page)">Delete</button>
                                      </div>
                                    </td>
                                  </tr>
                                }
                              </tbody>
                            </table>
                          </div>
                        }
                      </section>
                    }

                    @case ('versions') {
                      <section class="websites-page__surface">
                        <div class="websites-page__surface-header">
                          <div></div>
                          <button
                            type="button"
                            class="btn btn-sm btn-primary websites-page__surface-actions"
                            [disabled]="isSavingWebsiteVersion"
                            (click)="saveWebsiteVersion()"
                          >
                            {{ isSavingWebsiteVersion ? 'Saving version...' : 'Save version' }}
                          </button>
                        </div>

                        @if (isVersionsLoading) {
                          <div class="py-4 text-center">
                            <div class="spinner-border text-primary" role="status"></div>
                            <p class="mt-3 mb-0">Loading saved versions...</p>
                          </div>
                        } @else if (versionsLoadError) {
                          <app-api-load-error
                            title="Could not load website versions"
                            [message]="versionsLoadError"
                            retryLabel="Retry"
                            (retry)="loadWebsiteVersions(true)"
                          ></app-api-load-error>
                        } @else if (versions.length === 0) {
                          <div class="empty-state">No saved website versions yet. Save a version before a substantial change or before you publish.</div>
                        } @else {
                          <div class="websites-page__revision-list">
                            @for (version of versions; track trackWebsiteVersion($index, version)) {
                              <article class="websites-page__revision-card">
                                <div class="d-flex justify-content-between align-items-start gap-3">
                                  <div>
                                    <h4 class="websites-page__revision-title mb-1">Version {{ version.versionNumber }}</h4>
                                    <p class="websites-page__revision-copy mb-0">{{ version.note || 'Manual website save' }}</p>
                                  </div>
                                  <button
                                    type="button"
                                    class="btn btn-sm btn-outline-secondary"
                                    [disabled]="!!restoringWebsiteVersionPublicId"
                                    (click)="restoreWebsiteVersion(version)"
                                  >
                                    {{ restoringWebsiteVersionPublicId === version.publicId ? 'Restoring...' : 'Restore' }}
                                  </button>
                                </div>

                                <div class="websites-page__revision-meta">
                                  <span>{{ version.pageCount }} {{ version.pageCount === 1 ? 'page' : 'pages' }}</span>
                                  <span>{{ version.dataCollectionCount }} {{ version.dataCollectionCount === 1 ? 'collection' : 'collections' }}</span>
                                  <span>{{ version.translationCount }} {{ version.translationCount === 1 ? 'translation' : 'translations' }}</span>
                                  <span>{{ version.createdAt | date: 'medium' }}</span>
                                </div>
                              </article>
                            }
                          </div>
                        }
                      </section>
                    }

                    @case ('deployments') {
                      <section class="websites-page__surface">
                        <div class="websites-page__surface-header">
                          <div class="d-flex flex-wrap gap-2 websites-page__surface-actions">
                            <button type="button" class="btn btn-sm btn-outline-success" (click)="publishWebsite(selectedWebsite)" [disabled]="isPublishingWebsite">
                              {{ isPublishingWebsite ? 'Publishing...' : 'Publish now' }}
                            </button>
                            @if (selectedWebsite.subdomain) {
                              <button type="button" class="btn btn-sm btn-outline-success" (click)="openLiveWebsite(selectedWebsite)">Open live site</button>
                            }
                          </div>
                        </div>

                        @if (isDeploymentsLoading) {
                          <div class="py-4 text-center">
                            <div class="spinner-border text-primary" role="status"></div>
                            <p class="mt-3 mb-0">Loading deployments...</p>
                          </div>
                        } @else if (deploymentsLoadError) {
                          <app-api-load-error
                            title="Could not load deployments"
                            [message]="deploymentsLoadError"
                            retryLabel="Retry"
                            (retry)="loadWebsites()"
                          ></app-api-load-error>
                        } @else if (deployments.length === 0) {
                          <div class="empty-state">This website has not been published yet. Publish it to create the first live deployment snapshot.</div>
                        } @else {
                          <div class="websites-page__revision-list">
                            @for (deployment of deployments; track trackDeployment($index, deployment)) {
                              <article class="websites-page__revision-card">
                                <div class="d-flex justify-content-between align-items-start gap-3">
                                  <div>
                                    <h4 class="websites-page__revision-title mb-1">Deployment v{{ deployment.deploymentNumber }}</h4>
                                    <p class="websites-page__revision-copy mb-0">{{ deployment.subdomain }}</p>
                                  </div>
                                  <div class="d-flex flex-wrap justify-content-end gap-1">
                                    @if (deployment.status === 'active') {
                                      <span class="badge text-bg-success">Live</span>
                                    } @else {
                                      <span class="badge text-bg-secondary">Superseded</span>
                                    }
                                  </div>
                                </div>

                                <div class="websites-page__revision-meta">
                                  <span>{{ deployment.pageCount }} pages</span>
                                  <span>{{ deployment.createdAt | date: 'medium' }}</span>
                                  <span>{{ deployment.createdByUserPublicId || 'Unknown creator' }}</span>
                                </div>
                              </article>
                            }
                          </div>
                        }
                      </section>
                    }

                    @case ('data') {
                      <section class="websites-page__surface websites-page__data-surface">
                        <div class="websites-page__surface-header">
                          <div class="d-flex flex-wrap gap-2 websites-page__surface-actions">
                            <button type="button" class="btn btn-sm btn-outline-secondary" (click)="loadDataCollections(true)">Refresh</button>
                            <button type="button" class="btn btn-sm btn-primary" (click)="startNewDataCollection()">New collection</button>
                          </div>
                        </div>

                        @if (isDataCollectionsLoading) {
                          <div class="py-4 text-center">
                            <div class="spinner-border text-primary" role="status"></div>
                            <p class="mt-3 mb-0">Loading data collections...</p>
                          </div>
                        } @else if (dataCollectionsLoadError) {
                          <app-api-load-error
                            title="Could not load data collections"
                            [message]="dataCollectionsLoadError"
                            retryLabel="Retry"
                            (retry)="loadDataCollections(true)"
                          ></app-api-load-error>
                        } @else {
                          @if (dataCollectionsMessage) {
                            <div
                              class="alert mb-0"
                              [class.alert-info]="!dataCollectionsMessage.toLowerCase().includes('required') && !dataCollectionsMessage.toLowerCase().includes('invalid') && !dataCollectionsMessage.toLowerCase().includes('must') && !dataCollectionsMessage.toLowerCase().includes('could')"
                              [class.alert-danger]="dataCollectionsMessage.toLowerCase().includes('required') || dataCollectionsMessage.toLowerCase().includes('invalid') || dataCollectionsMessage.toLowerCase().includes('must') || dataCollectionsMessage.toLowerCase().includes('could')"
                            >
                              {{ dataCollectionsMessage }}
                            </div>
                          }

                          <div class="websites-page__data-grid">
                            <aside class="websites-page__data-list" aria-label="Data collections">
                              @if (dataCollections.length === 0) {
                                <div class="empty-state">No collections yet. Add products, filters, categories, or any JSON data that repeaters can render.</div>
                              } @else {
                                @for (collection of dataCollections; track trackDataCollection($index, collection)) {
                                  <button
                                    type="button"
                                    class="websites-page__data-item"
                                    [class.is-active]="collection.publicId === selectedDataCollectionPublicId"
                                    (click)="selectDataCollection(collection.publicId)"
                                  >
                                    <span class="websites-page__data-item-key">{{ collection.key }}</span>
                                    <strong>{{ collection.label }}</strong>
                                    <span>{{ collection.items.length }} items</span>
                                  </button>
                                }
                              }
                            </aside>

                            <form class="websites-page__data-editor" (ngSubmit)="saveDataCollection()">
                              <div class="websites-page__email-card-header">
                                <div>
                                  <h5 class="websites-page__email-title bw-title-with-info">
                                    {{ selectedDataCollection ? 'Edit collection' : 'New collection' }}
                                    <app-info-tooltip compact placement="right" text="Use stable keys such as products, categories, or filters. Builder dynamic components select these keys from a dropdown."></app-info-tooltip>
                                  </h5>
                                </div>
                                @if (selectedDataCollection) {
                                  <button
                                    type="button"
                                    class="btn btn-sm btn-outline-danger"
                                    [disabled]="isSavingDataCollections"
                                    (click)="deleteDataCollection(selectedDataCollection)"
                                  >
                                    Delete
                                  </button>
                                }
                              </div>

                              <div class="websites-page__form-grid">
                                <label>
                                  <span class="form-label">Key</span>
                                  <input
                                    class="form-control"
                                    [(ngModel)]="dataCollectionForm.key"
                                    name="data_collection_key"
                                    placeholder="products"
                                    autocomplete="off"
                                    autocapitalize="off"
                                    spellcheck="false"
                                  />
                                </label>
                                <label>
                                  <span class="form-label">Label</span>
                                  <input class="form-control" [(ngModel)]="dataCollectionForm.label" name="data_collection_label" placeholder="Products" />
                                </label>
                              </div>

                              <label>
                                <span class="form-label">Description</span>
                                <input
                                  class="form-control"
                                  [(ngModel)]="dataCollectionForm.description"
                                  name="data_collection_description"
                                  placeholder="Catalog products rendered by product grid components"
                                />
                              </label>

                              <label>
                                <span class="form-label">Items JSON array</span>
                                <textarea
                                  class="form-control websites-page__data-textarea"
                                  [(ngModel)]="dataCollectionForm.itemsJson"
                                  name="data_collection_items"
                                  rows="16"
                                  spellcheck="false"
                                ></textarea>
                              </label>

                              <label>
                                <span class="form-label">Schema / editor hints JSON object</span>
                                <textarea
                                  class="form-control websites-page__data-textarea websites-page__data-textarea--small"
                                  [(ngModel)]="dataCollectionForm.schemaJson"
                                  name="data_collection_schema"
                                  rows="7"
                                  spellcheck="false"
                                ></textarea>
                              </label>

                              <div class="websites-page__email-actions">
                                <button type="submit" class="btn btn-primary" [disabled]="isSavingDataCollections">
                                  {{ isSavingDataCollections ? 'Saving...' : 'Save collection' }}
                                </button>
                                <button type="button" class="btn btn-outline-secondary" [disabled]="isSavingDataCollections" (click)="startNewDataCollection()">Clear form</button>
                              </div>
                            </form>
                          </div>
                        }
                      </section>
                    }

                    @case ('analytics') {
                      <section class="websites-page__surface">
                        <app-website-analytics-panel [websitePublicId]="selectedWebsite.publicId"></app-website-analytics-panel>
                      </section>
                    }

                    @case ('emails') {
                      <section class="websites-page__surface websites-page__email-surface">
                        <div class="websites-page__surface-header">
                          <button type="button" class="btn btn-sm btn-outline-secondary websites-page__surface-actions" (click)="loadEmailConfiguration(true)">
                            Refresh
                          </button>
                        </div>

                        @if (isEmailConfigurationLoading) {
                          <div class="py-4 text-center">
                            <div class="spinner-border text-primary" role="status"></div>
                            <p class="mt-3 mb-0">Loading email settings...</p>
                          </div>
                        } @else if (emailConfigurationLoadError) {
                          <app-api-load-error
                            title="Could not load store email settings"
                            [message]="emailConfigurationLoadError"
                            retryLabel="Retry"
                            (retry)="loadEmailConfiguration(true)"
                          ></app-api-load-error>
                        } @else {
                          @if (emailSettingsMessage) {
                            <div class="alert mb-0" [class.alert-info]="!emailSettingsMessage.toLowerCase().includes('required') && !emailSettingsMessage.toLowerCase().includes('could')" [class.alert-danger]="emailSettingsMessage.toLowerCase().includes('required') || emailSettingsMessage.toLowerCase().includes('could')">
                              {{ emailSettingsMessage }}
                            </div>
                          }

                          <div class="websites-page__email-grid">
                            <form class="websites-page__email-card" (ngSubmit)="saveEmailSettings()">
                              <div class="websites-page__email-card-header">
                                <div>
                                  <h5 class="websites-page__email-title bw-title-with-info">
                                    SMTP delivery
                                    <app-info-tooltip compact placement="right" text="Uses the MXroute SMTP API shape. The password is encrypted before storage."></app-info-tooltip>
                                  </h5>
                                </div>
                                <span class="badge" [class.text-bg-success]="emailSettings?.passwordConfigured" [class.text-bg-secondary]="!emailSettings?.passwordConfigured">
                                  {{ emailSettings?.passwordConfigured ? 'Password saved' : 'No password' }}
                                </span>
                              </div>

                              @if (emailSettings && !emailSettings.encryptionConfigured) {
                                <div class="alert alert-warning mb-0">
                                  Add STORE_SMTP_PASSWORD_ENCRYPTION_KEY before saving SMTP passwords.
                                </div>
                              }

                              <label class="form-check form-switch websites-page__switch-row">
                                <input class="form-check-input" type="checkbox" [(ngModel)]="emailSettingsForm.enabled" name="email_enabled" />
                                <span class="form-check-label">Use this website's SMTP settings for storefront emails</span>
                              </label>

                              <div class="websites-page__form-grid">
                                <label>
                                  <span class="form-label">SMTP API URL</span>
                                  <input class="form-control" [(ngModel)]="emailSettingsForm.apiUrl" name="email_api_url" placeholder="https://smtpapi.mxroute.com/" />
                                </label>
                                <label>
                                  <span class="form-label">SMTP server</span>
                                  <input class="form-control" [(ngModel)]="emailSettingsForm.smtpServer" name="email_smtp_server" placeholder="redbull.mxrouting.net" />
                                </label>
                                <label>
                                  <span class="form-label">SMTP username</span>
                                  <input class="form-control" [(ngModel)]="emailSettingsForm.smtpUsername" name="email_smtp_username" autocomplete="off" />
                                </label>
                                <label>
                                  <span class="form-label">SMTP password</span>
                                  <input class="form-control" type="password" [(ngModel)]="emailSettingsForm.smtpPassword" name="email_smtp_password" autocomplete="new-password" placeholder="Leave blank to keep current password" />
                                </label>
                                <label>
                                  <span class="form-label">From email</span>
                                  <input class="form-control" type="email" [(ngModel)]="emailSettingsForm.fromEmail" name="email_from_email" />
                                </label>
                                <label>
                                  <span class="form-label">From name</span>
                                  <input class="form-control" [(ngModel)]="emailSettingsForm.fromName" name="email_from_name" [placeholder]="selectedWebsite.name" />
                                </label>
                              </div>

                              <div class="websites-page__email-options">
                                <label class="form-check">
                                  <input class="form-check-input" type="checkbox" [(ngModel)]="emailSettingsForm.fallbackToPlatform" name="email_fallback" />
                                  <span class="form-check-label">Fallback to BizzWeaver platform email if this sender fails</span>
                                </label>
                                <label class="form-check">
                                  <input class="form-check-input" type="checkbox" [(ngModel)]="emailSettingsForm.clearSmtpPassword" name="email_clear_password" />
                                  <span class="form-check-label">Clear saved SMTP password</span>
                                </label>
                              </div>

                              <div class="websites-page__email-actions">
                                <button type="submit" class="btn btn-primary" [disabled]="isSavingEmailSettings">
                                  {{ isSavingEmailSettings ? 'Saving...' : 'Save SMTP settings' }}
                                </button>
                              </div>
                            </form>

                            <section class="websites-page__email-card">
                              <div class="websites-page__email-card-header">
                                <div>
                                  <h5 class="websites-page__email-title bw-title-with-info">
                                    Send test
                                    <app-info-tooltip compact placement="right" text="Send the selected transactional template through the currently saved sender settings."></app-info-tooltip>
                                  </h5>
                                </div>
                              </div>

                              <div class="websites-page__form-grid websites-page__form-grid--single">
                                <label>
                                  <span class="form-label">Recipient</span>
                                  <input class="form-control" type="email" [(ngModel)]="emailSettingsForm.testRecipient" name="email_test_recipient" placeholder="you@example.com" />
                                </label>
                                <label>
                                  <span class="form-label">Template</span>
                                  <select class="form-select" [(ngModel)]="emailSettingsForm.testTemplateKey" name="email_test_template">
                                    @for (template of emailTemplates; track template.key) {
                                      <option [ngValue]="template.key">{{ template.label }}</option>
                                    }
                                  </select>
                                </label>
                              </div>

                              <button type="button" class="btn btn-outline-primary" [disabled]="isSendingTestEmail" (click)="sendEmailTest()">
                                {{ isSendingTestEmail ? 'Sending...' : 'Send test email' }}
                              </button>
                            </section>
                          </div>

                          <section class="websites-page__email-card websites-page__email-card--templates">
                            <div class="websites-page__email-card-header">
                              <div>
                                <h5 class="websites-page__email-title bw-title-with-info">
                                  Transactional templates
                                  <app-info-tooltip compact placement="right" text="Use variables such as storeName, customerName, resetUrl, orderPublicId, orderItemsHtml, and orderTotal in these templates."></app-info-tooltip>
                                </h5>
                              </div>
                            </div>

                            <div class="websites-page__email-template-layout">
                              <div class="websites-page__email-template-list" role="tablist" aria-label="Email templates">
                                @for (template of emailTemplates; track template.key) {
                                  <button
                                    type="button"
                                    class="websites-page__email-template-button"
                                    [class.is-active]="template.key === selectedEmailTemplateKey"
                                    (click)="selectEmailTemplate(template.key)"
                                  >
                                    <strong>{{ template.label }}</strong>
                                    <span>{{ template.enabled ? 'Enabled' : 'Disabled' }}</span>
                                  </button>
                                }
                              </div>

                              <form class="websites-page__email-template-editor" (ngSubmit)="saveEmailTemplate()">
                                <label class="form-check form-switch websites-page__switch-row">
                                  <input class="form-check-input" type="checkbox" [(ngModel)]="emailTemplateForm.enabled" name="email_template_enabled" />
                                  <span class="form-check-label">Send this email</span>
                                </label>
                                <label>
                                  <span class="form-label">Subject</span>
                                  <input class="form-control" [(ngModel)]="emailTemplateForm.subject" name="email_template_subject" />
                                </label>
                                <label>
                                  <span class="form-label">HTML body</span>
                                  <textarea class="form-control websites-page__email-template-textarea" [(ngModel)]="emailTemplateForm.htmlBody" name="email_template_html" rows="8"></textarea>
                                </label>
                                <label>
                                  <span class="form-label">Plain text body</span>
                                  <textarea class="form-control websites-page__email-template-textarea" [(ngModel)]="emailTemplateForm.textBody" name="email_template_text" rows="4"></textarea>
                                </label>
                                <div class="websites-page__email-actions">
                                  <button type="submit" class="btn btn-primary" [disabled]="isSavingEmailTemplate">
                                    {{ isSavingEmailTemplate ? 'Saving...' : 'Save template' }}
                                  </button>
                                </div>
                              </form>
                            </div>
                          </section>
                        }
                      </section>
                    }
                  }
                  </div>
                </div>
                </div>
              } @else {
                <div class="websites-page__panel-scroll">
                  <div class="empty-state">Select a website from the left inventory to open its workspace.</div>
                </div>
              }
            </div>
          </section>
        </div>
      }

      @if (activeModal) {
        <button type="button" class="websites-page__modal-scrim" aria-label="Close modal" (click)="closeModal()"></button>
        <div class="websites-page__modal">
          <section class="page-card card websites-page__modal-dialog" [class.websites-page__modal-dialog--wide]="activeModal === 'createWebsite'">
            <div class="card-body d-grid gap-3">
              <div class="d-flex justify-content-between align-items-start gap-3">
                <div>
                  <h3 class="section-title mb-1 bw-title-with-info">
                    @switch (activeModal) {
                      @case ('createWebsite') { Create website }
                      @case ('editWebsite') { Edit website }
                      @case ('createPage') { Create page }
                      @case ('editPage') { Edit page }
                    }
                    <app-info-tooltip
                      compact
                      placement="right"
                      [text]="activeModal === 'createWebsite'
                        ? 'Start a new website record and create its first home page draft.'
                        : activeModal === 'editWebsite'
                          ? 'Update website metadata, status, and descriptive details.'
                          : activeModal === 'createPage'
                            ? 'Add a new page and create its first editable draft immediately.'
                            : 'Update page metadata and optionally promote it to the home page.'"
                    ></app-info-tooltip>
                  </h3>
                </div>
                <button type="button" class="btn btn-sm btn-outline-secondary" (click)="closeModal()">Close</button>
              </div>

              @if (modalError) {
                <div class="alert alert-danger mb-0">{{ modalError }}</div>
              }

              @if (activeModal === 'createWebsite' || activeModal === 'editWebsite') {
                <form class="d-grid gap-3" (ngSubmit)="activeModal === 'createWebsite' ? createWebsite() : saveWebsite()">
                  @if (activeModal === 'createWebsite') {
                    <div class="d-grid gap-3">
                      <div>
                        <h4 class="section-title mb-1 websites-page__title-line">
                          <span>Starter templates</span>
                          <app-info-tooltip
                            compact
                            placement="right"
                            text="Pick a built-in website starter. The API creates the matching starter pages and initial page drafts from this template when the website is created."
                          ></app-info-tooltip>
                        </h4>
                      </div>

                      <div class="websites-page__template-grid">
                        @for (template of websiteTemplates; track template.id) {
                          <article
                            role="button"
                            tabindex="0"
                            class="websites-page__template-card"
                            [attr.aria-pressed]="selectedWebsiteTemplateId === template.id"
                            [class.is-active]="selectedWebsiteTemplateId === template.id"
                            (click)="selectWebsiteTemplate(template.id)"
                            (keydown.enter)="selectWebsiteTemplate(template.id)"
                            (keydown.space)="selectWebsiteTemplate(template.id); $event.preventDefault()"
                          >
                            <div class="d-flex justify-content-between align-items-start gap-3">
                              <div>
                                <h5 class="websites-page__template-title mb-1">{{ template.name }}</h5>
                                <div class="d-flex flex-wrap gap-1 mb-2">
                                  <span class="websites-page__template-chip">{{ template.category }}</span>
                                  <span class="websites-page__template-chip">{{ template.industry }}</span>
                                </div>
                                <p class="websites-page__template-copy mb-0">{{ template.description }}</p>
                              </div>
                              <div class="d-grid gap-1 justify-items-end">
                                <span class="badge text-bg-secondary">{{ template.pageCount }} pages</span>
                                @if (selectedWebsiteTemplateId === template.id) {
                                  <span class="websites-page__template-selected">Selected</span>
                                }
                              </div>
                            </div>

                            <div class="websites-page__template-audience">{{ template.audience }}</div>

                            <div class="websites-page__template-highlights">
                              @for (highlight of template.highlights; track highlight) {
                                <span class="websites-page__template-chip">{{ highlight }}</span>
                              }
                            </div>
                            <button
                              type="button"
                              class="btn btn-outline-secondary btn-sm websites-page__template-preview"
                              (click)="previewWebsiteTemplate(template.id, $event)"
                            >
                              Preview template
                            </button>
                          </article>
                        }
                      </div>

                      <div class="websites-page__template-summary">
                        <div class="d-flex flex-wrap justify-content-between align-items-start gap-3">
                          <div>
                            <p class="websites-page__template-summary-label mb-1">Selected template</p>
                            <h5 class="websites-page__template-summary-title mb-1">{{ selectedWebsiteTemplate.name }}</h5>
                            <p class="websites-page__template-summary-copy mb-0">{{ selectedWebsiteTemplate.description }}</p>
                          </div>
                          <span class="badge text-bg-primary">{{ selectedWebsiteTemplate.pageCount }} starter pages</span>
                        </div>

                        <div class="websites-page__template-pages">
                          @for (pageLabel of selectedWebsiteTemplate.pageLabels; track pageLabel) {
                            <span class="websites-page__template-page-chip">{{ pageLabel }}</span>
                          }
                        </div>
                      </div>
                    </div>
                  }

                  <div>
                    <label class="form-label">
                      Website name
                      @if (activeModal === 'createWebsite') {
                        <app-info-tooltip
                          compact
                          placement="right"
                          text="This name is also used as the initial context for the starter pages created from the selected template."
                        ></app-info-tooltip>
                      }
                    </label>
                    <input class="form-control" type="text" [(ngModel)]="websiteForm.name" name="website_name" required />
                  </div>

                  <div>
                    <label class="form-label">Slug</label>
                    <input class="form-control" type="text" [(ngModel)]="websiteForm.slug" name="website_slug" placeholder="Auto-generated if empty" />
                  </div>

                  <div>
                    <label class="form-label">Description</label>
                    <textarea class="form-control" rows="4" [(ngModel)]="websiteForm.description" name="website_description"></textarea>
                  </div>

                  <div>
                    <label class="form-label">Browser tab title</label>
                    <input class="form-control" type="text" [(ngModel)]="websiteForm.browserTitle" name="website_browser_title" placeholder="Defaults to page title and website name" />
                  </div>

                  <div>
                    <label class="form-label">Favicon URL</label>
                    <input class="form-control" type="url" [(ngModel)]="websiteForm.faviconUrl" name="website_favicon_url" placeholder="https://example.com/favicon.png" />
                    <div class="form-text">Used as the deployed website browser icon when the site is published.</div>
                  </div>

                  @if (activeModal === 'editWebsite') {
                    <div>
                      <label class="form-label">Status</label>
                      <select class="form-select" [(ngModel)]="websiteForm.status" name="website_status">
                        @for (status of websiteStatuses; track status) {
                          <option [ngValue]="status">{{ status }}</option>
                        }
                      </select>
                    </div>
                  }

                  <div class="d-flex flex-wrap justify-content-end gap-2">
                    <button type="button" class="btn btn-outline-secondary" (click)="closeModal()">Cancel</button>
                    <button class="btn btn-primary" type="submit" [disabled]="isSubmittingModal">
                      {{ isSubmittingModal ? 'Saving...' : (activeModal === 'createWebsite' ? 'Create website' : 'Save changes') }}
                    </button>
                  </div>
                </form>
              } @else {
                <form class="d-grid gap-3" (ngSubmit)="activeModal === 'createPage' ? createPage() : savePage()">
                  <div>
                    <label class="form-label">Page name</label>
                    <input class="form-control" type="text" [(ngModel)]="pageForm.name" name="page_name" required />
                  </div>

                  <div>
                    <label class="form-label">Slug</label>
                    <input class="form-control" type="text" [(ngModel)]="pageForm.slug" name="page_slug" placeholder="Auto-generated if empty" />
                  </div>

                  <div>
                    <label class="form-label">SEO title</label>
                    <input class="form-control" type="text" [(ngModel)]="pageForm.seoTitle" name="page_seo_title" />
                  </div>

                  <div>
                    <label class="form-label">SEO description</label>
                    <textarea class="form-control" rows="4" [(ngModel)]="pageForm.seoDescription" name="page_seo_description"></textarea>
                  </div>

                  <label class="form-check">
                    <input class="form-check-input" type="checkbox" [(ngModel)]="pageForm.isHome" name="page_is_home" />
                    <span class="form-check-label">Set as home page</span>
                  </label>

                  <div class="d-flex flex-wrap justify-content-end gap-2">
                    <button type="button" class="btn btn-outline-secondary" (click)="closeModal()">Cancel</button>
                    <button class="btn btn-primary" type="submit" [disabled]="isSubmittingModal">
                      {{ isSubmittingModal ? 'Saving...' : (activeModal === 'createPage' ? 'Create page' : 'Save changes') }}
                    </button>
                  </div>
                </form>
              }
            </div>
          </section>
        </div>
      }
    </div>
  }
</div>
